import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Modal } from '../../components/ui/Modal';
import { RiskBadge } from '../../components/ui/StatusBadge';
import { useAdminLayoutContext } from '../../components/admin/AdminLayout';
import {
  Plus,
  Edit2,
  Trash2,
  Brain,
  AlertTriangle,
  CheckCircle,
  Clock,
  Info,
  ArrowUpDown,
  Filter,
} from 'lucide-react';

type ContentStatus = 'draft' | 'pending' | 'published';

interface SymptomGroup {
  id: string;
  name_no: string;
  name_en?: string;
  color_tag?: string;
  order_index: number;
  status: ContentStatus;
}

interface SymptomWithGroup {
  id: string;
  group_id: string;
  name_no: string;
  name_en?: string;
  help_text?: string;
  risk_tag?: string;
  order_index?: number;
  status: ContentStatus;
  version?: number;
  group?: SymptomGroup;
}

export function SymptomManagement() {
  const { profile, hasRole } = useAuth();
  const { setHeaderAction, setPageTitle } = useAdminLayoutContext();
  const [showModal, setShowModal] = useState(false);
  const [editingSymptom, setEditingSymptom] = useState<SymptomWithGroup | null>(null);
  const [filterStatus, setFilterStatus] = useState<ContentStatus | 'all'>('all');
  const [filterGroup, setFilterGroup] = useState<string>('all');
  const [symptomsData, setSymptomsData] = useState<SymptomWithGroup[] | undefined>(undefined);
  const [publishedGroups, setPublishedGroups] = useState<SymptomGroup[] | undefined>(undefined);
  const [formData, setFormData] = useState({
    group_id: '',
    name_no: '',
    help_text: '',
    risk_tag: '',
  });

  const canEdit = hasRole(['superadmin', 'org_admin', 'editor']);

  // Set page title and header action
  useEffect(() => {
    setPageTitle('Symptomer');

    if (canEdit) {
      setHeaderAction(
        <button
          onClick={() => {
            setEditingSymptom(null);
            setFormData({
              group_id: '',
              name_no: '',
              help_text: '',
              risk_tag: '',
            });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Nytt symptom
        </button>
      );
    }

    return () => {
      setPageTitle(null);
      setHeaderAction(null);
    };
  }, [setHeaderAction, setPageTitle, canEdit]);

  const riskOptions = [
    { value: '', label: 'Ingen risiko' },
    { value: 'suicide', label: 'Selvmordsrisiko' },
    { value: 'violence', label: 'Voldsrisiko' },
    { value: 'self-harm', label: 'Selvskading' },
    { value: 'psychosis', label: 'Psykose' },
    { value: 'substance', label: 'Rusmisbruk' },
  ];

  // Fetch symptoms with their groups
  const fetchSymptoms = useCallback(async () => {
    let query = supabase
      .from('symptoms')
      .select('*, symptom_groups(id, name_no, name_en, color_tag, order_index, status)')
      .order('order_index', { ascending: true });

    if (filterStatus !== 'all') {
      query = query.eq('status', filterStatus);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching symptoms:', error);
      return;
    }

    const mapped = (data || []).map((s: any) => ({
      ...s,
      group: s.symptom_groups || undefined,
      symptom_groups: undefined,
    }));
    setSymptomsData(mapped);
  }, [filterStatus]);

  // Fetch published groups for dropdown
  const fetchPublishedGroups = useCallback(async () => {
    const { data, error } = await supabase
      .from('symptom_groups')
      .select('id, name_no, name_en, color_tag, order_index, status')
      .eq('status', 'published')
      .order('order_index', { ascending: true });

    if (error) {
      console.error('Error fetching groups:', error);
      return;
    }
    setPublishedGroups(data || []);
  }, []);

  useEffect(() => {
    fetchSymptoms();
  }, [fetchSymptoms]);

  useEffect(() => {
    fetchPublishedGroups();
  }, [fetchPublishedGroups]);

  // Filter symptoms
  const symptoms = symptomsData
    ? filterGroup !== 'all'
      ? symptomsData.filter((s) => s.group_id === filterGroup)
      : symptomsData
    : [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingSymptom) {
        const { error } = await supabase
          .from('symptoms')
          .update({
            group_id: formData.group_id,
            name_no: formData.name_no,
            help_text: formData.help_text || null,
            risk_tag: formData.risk_tag || null,
          })
          .eq('id', editingSymptom.id);

        if (error) throw error;
      } else {
        // New symptoms are placed at the end of the list within their group
        const groupSymptoms = symptomsData?.filter(s => s.group_id === formData.group_id) || [];
        const maxOrderIndex = groupSymptoms.reduce((max, s) => Math.max(max, s.order_index || 0), -1);

        const { error } = await supabase
          .from('symptoms')
          .insert({
            group_id: formData.group_id,
            name_no: formData.name_no,
            help_text: formData.help_text || null,
            risk_tag: formData.risk_tag || null,
            order_index: maxOrderIndex + 1,
          });

        if (error) throw error;
      }

      setShowModal(false);
      setEditingSymptom(null);
      resetForm();
      fetchSymptoms();
    } catch (error: any) {
      console.error('Error saving symptom:', error);
      alert(`Feil: ${error.message}`);
    }
  };

  const handleStatusChange = async (id: string, newStatus: ContentStatus) => {
    try {
      const { error } = await supabase
        .from('symptoms')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;
      fetchSymptoms();
    } catch (error: any) {
      console.error('Error updating status:', error);
      alert(`Feil: ${error.message}`);
    }
  };

  const handleEdit = (symptom: SymptomWithGroup) => {
    setEditingSymptom(symptom);
    setFormData({
      group_id: symptom.group_id,
      name_no: symptom.name_no,
      help_text: symptom.help_text || '',
      risk_tag: symptom.risk_tag || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Er du sikker? Dette vil også slette alle koblinger til tilnærminger.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('symptoms')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchSymptoms();
    } catch (error: any) {
      console.error('Error deleting symptom:', error);
      alert(`Feil: ${error.message}`);
    }
  };

  const handleReorder = async (id: string, direction: 'up' | 'down', withinGroup: SymptomWithGroup[]) => {
    const index = withinGroup.findIndex((s) => s.id === id);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === withinGroup.length - 1)) {
      return;
    }

    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const currentOrder = withinGroup[index].order_index || 0;
    const swapOrder = withinGroup[swapIndex].order_index || 0;

    try {
      await supabase
        .from('symptoms')
        .update({ order_index: swapOrder })
        .eq('id', withinGroup[index].id);

      await supabase
        .from('symptoms')
        .update({ order_index: currentOrder })
        .eq('id', withinGroup[swapIndex].id);

      fetchSymptoms();
    } catch (error: any) {
      console.error('Error reordering:', error);
      alert(`Feil: ${error.message}`);
    }
  };

  const resetForm = () => {
    setFormData({
      group_id: publishedGroups?.[0]?.id || '',
      name_no: '',
      help_text: '',
      risk_tag: '',
    });
  };

  if (symptomsData === undefined) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Laster symptomer...</div>
      </div>
    );
  }

  // Group symptoms by group_id
  const groupedSymptoms = symptoms.reduce((acc, symptom) => {
    const groupId = symptom.group_id;
    if (!acc[groupId]) {
      acc[groupId] = [];
    }
    acc[groupId].push(symptom);
    return acc;
  }, {} as Record<string, SymptomWithGroup[]>);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex items-center gap-4 flex-1">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
            <div className="flex gap-2">
              {(['all', 'draft', 'published'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    filterStatus === status
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {status === 'all' ? 'Alle' : status === 'draft' ? 'Utkast' : 'Publisert'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <select
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Alle grupper</option>
              {publishedGroups?.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name_no}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Symptoms grouped by category */}
      <div className="space-y-6">
        {Object.entries(groupedSymptoms).map(([groupId, groupSymptoms]) => {
          const group = groupSymptoms[0]?.group;
          if (!group) return null;

          return (
            <div key={groupId} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 overflow-hidden">
              <div
                className="px-6 py-3 border-b dark:border-gray-700 flex items-center gap-3"
                style={{ backgroundColor: `${group.color_tag}20` }}
              >
                <div
                  className="h-8 w-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: group.color_tag }}
                >
                  <Brain className="h-4 w-4 text-white" />
                </div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">{group.name_no}</h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">({groupSymptoms.length} symptomer)</span>
              </div>

              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {groupSymptoms.map((symptom, index) => (
                  <div key={symptom.id} className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleReorder(symptom.id, 'up', groupSymptoms)}
                            disabled={index === 0}
                            className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30"
                          >
                            <ArrowUpDown className="h-4 w-4" />
                          </button>
                          <span className="text-sm text-gray-500 dark:text-gray-400 w-8">{symptom.order_index}</span>
                        </div>

                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-gray-900 dark:text-white">{symptom.name_no}</span>
                            {symptom.risk_tag && (
                              <RiskBadge riskTag={symptom.risk_tag} />
                            )}
                          </div>
                          {symptom.help_text && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-start gap-1">
                              <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              {symptom.help_text}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {/* Clickable status badge */}
                        {symptom.status === 'published' ? (
                          <span
                            onClick={() => canEdit && handleStatusChange(symptom.id, 'draft')}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-md ${canEdit ? 'cursor-pointer hover:opacity-80' : ''}`}
                            title={canEdit ? 'Klikk for å avpublisere' : undefined}
                          >
                            <CheckCircle className="h-3 w-3" />
                            Publisert
                          </span>
                        ) : (
                          <span
                            onClick={() => canEdit && handleStatusChange(symptom.id, 'published')}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded-md ${canEdit ? 'cursor-pointer hover:opacity-80' : ''}`}
                            title={canEdit ? 'Klikk for å publisere' : undefined}
                          >
                            <Clock className="h-3 w-3" />
                            Utkast
                          </span>
                        )}
                        <div className="flex items-center gap-2">
                          {canEdit && (
                            <>
                              <button
                                onClick={() => handleEdit(symptom)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Rediger"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(symptom.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Slett"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {symptoms.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700">
          <Brain className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">Ingen symptomer funnet</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            {canEdit ? 'Opprett ditt første symptom' : 'Ingen symptomer matcher filteret'}
          </p>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingSymptom(null);
          resetForm();
        }}
        title={editingSymptom ? 'Rediger symptom' : 'Legg til nytt symptom'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="group_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Symptomgruppe *
            </label>
            <select
              id="group_id"
              required
              value={formData.group_id}
              onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Velg gruppe...</option>
              {publishedGroups?.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name_no}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="name_no" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Navn *
            </label>
            <input
              id="name_no"
              type="text"
              required
              value={formData.name_no}
              onChange={(e) => setFormData({ ...formData, name_no: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="help_text" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Hjelpetekst
            </label>
            <textarea
              id="help_text"
              rows={2}
              value={formData.help_text}
              onChange={(e) => setFormData({ ...formData, help_text: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Kort forklaring av symptomet..."
            />
          </div>

          <div>
            <label htmlFor="risk_tag" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Risiko-tag
            </label>
            <select
              id="risk_tag"
              value={formData.risk_tag}
              onChange={(e) => setFormData({ ...formData, risk_tag: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {riskOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {formData.risk_tag && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800 dark:text-yellow-200">
                  <p className="font-medium">Viktig om risiko-tags:</p>
                  <p className="mt-1">
                    Symptomer med risiko-tag vil utløse varsler i vurderingen når de velges med høy intensitet.
                    Sørg for at riktige kontaktpunkter og krisehåndtering er konfigurert.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                setEditingSymptom(null);
                resetForm();
              }}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {editingSymptom ? 'Oppdater' : 'Opprett'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
